import {
  QuestionNode,
  AnswerNode,
  QAPair,
  Dependency,
  DepthLevel,
  BranchStatus,
} from '../types';

/**
 * Parses markdown content with custom Q&A syntax into structured QAPair objects
 */
export function parseMarkdown(content: string): QAPair[] {
  const qaPairs: QAPair[] = [];

  // Extract success criteria from the header
  const successCriteriaMatch = content.match(/\*\*SUCCESS CRITERIA:\*\*\s*(.+?)(?:\n\n|---)/s);
  const successCriteriaText = successCriteriaMatch
    ? successCriteriaMatch[1].trim().replace(/\s+/g, ' ')
    : 'An MVP prototype of an E2EE mobile application for group messaging';

  // Create virtual root "Success Criteria" node
  const rootNode: QAPair = {
    question: {
      id: 'SUCCESS_CRITERIA',
      questionText: 'What does success look like for this project?',
      breadcrumb: 'Success Criteria',
      depth: 'L1',
    },
    answer: {
      id: 'SUCCESS_CRITERIA',
      answerText: successCriteriaText,
      dependencies: [],
      branchStatus: 'Explore deeper',
    },
    parentId: undefined, // True root
  };

  qaPairs.push(rootNode);

  // Split by markdown heading (### QUESTION)
  const questionBlocks = content.split(/###\s+QUESTION/);

  // Skip first block (it's the intro/header)
  for (let i = 1; i < questionBlocks.length; i++) {
    const block = questionBlocks[i].trim();
    if (!block) continue;

    try {
      const qaPair = parseQuestionAnswerPair(block);
      if (qaPair) {
        qaPairs.push(qaPair);
      }
    } catch (error) {
      console.error('Failed to parse Q&A block:', error);
    }
  }

  return qaPairs;
}

/**
 * Parse a complete Q&A pair from a block
 */
function parseQuestionAnswerPair(block: string): QAPair | null {
  // Extract node ID from [Root.Who.1] format
  const idMatch = block.match(/\[([^\]]+)\]/);
  if (!idMatch) return null;
  const id = idMatch[1];

  // Extract question text from **Question:** field
  const questionMatch = block.match(/\*\*Question:\*\*\s*(.+?)(?:\*\*|$)/s);
  const questionText = questionMatch ? questionMatch[1].trim().replace(/\s+/g, ' ') : '';

  // Extract breadcrumb from **Breadcrumb:** field
  const breadcrumbMatch = block.match(/\*\*Breadcrumb:\*\*\s*(.+?)(?:\*\*|$)/s);
  const breadcrumb = breadcrumbMatch ? breadcrumbMatch[1].trim().replace(/\s+/g, ' ') : '';

  // Extract depth from **Depth:** field
  const depthMatch = block.match(/\*\*Depth:\*\*\s*(L\d)/i);
  const depth = (depthMatch ? depthMatch[1] : 'L1') as DepthLevel;

  // Extract context (optional)
  const contextMatch = block.match(/\*\*Context:\*\*\s*(.+?)(?:\*\*|$)/s);
  const context = contextMatch ? contextMatch[1].trim().replace(/\s+/g, ' ') : undefined;

  // Create question node
  const question: QuestionNode = {
    id,
    questionText,
    breadcrumb,
    depth,
    context,
  };

  // Extract answer text from **ANSWER [...]:**
  const answerMatch = block.match(/\*\*ANSWER\s+\[[^\]]+\]:\*\*\s*(.+?)(?:\*\*|$)/s);
  const answerText = answerMatch ? answerMatch[1].trim().replace(/\s+/g, ' ') : '';

  // Extract dependencies from **Dependencies:**
  const depsMatch = block.match(/\*\*Dependencies:\*\*\s*(.+?)(?:\*\*|$|---)/s);
  const dependencies: Dependency[] = [];

  if (depsMatch) {
    const depsText = depsMatch[1].trim();
    if (depsText.toLowerCase() !== 'none') {
      dependencies.push(...parseDependencies(depsText));
    }
  }

  // Extract branch status (optional, default to "Sufficient")
  const branchMatch = block.match(/\*\*Branch Status:\*\*\s*(.+?)(?:\*\*|$)/s);
  const branchStatus = (branchMatch ? branchMatch[1].trim() : 'Sufficient') as BranchStatus;

  // Create answer node
  const answer: AnswerNode = {
    id,
    answerText,
    dependencies,
    branchStatus,
  };

  // Determine parent ID
  const parentId = getParentId(id);

  return {
    question,
    answer,
    parentId,
  };
}


/**
 * Parse dependency list from text
 * Format: [Type.#-description (context)], [Type.#-description (context)]
 */
function parseDependencies(depsText: string): Dependency[] {
  const dependencies: Dependency[] = [];

  // Match patterns like [How.1-architecture (privacy-focused users affect tech choices)]
  const depRegex = /\[([^\]]+)\]/g;
  let match;

  while ((match = depRegex.exec(depsText)) !== null) {
    const rawText = match[1];

    // Parse targetId-description (context)
    const parts = rawText.match(/^([^-\s]+)\s*-\s*([^(]+?)(?:\s*\(([^)]+)\))?$/);

    if (parts) {
      dependencies.push({
        targetId: parts[1].trim(),
        description: parts[2].trim(),
        context: parts[3]?.trim(),
        rawText,
      });
    } else {
      // Fallback: just use the whole thing as targetId
      dependencies.push({
        targetId: rawText.trim(),
        description: '',
        rawText,
      });
    }
  }

  return dependencies;
}

/**
 * Determine parent ID from a node ID
 * Works for any depth level (L1 through L5+)
 *
 * Examples:
 * - "Root.Who.1" -> "SUCCESS_CRITERIA"
 * - "Who.1.How.1" -> "Root.Who.1"
 * - "Who.1.How.1.What.1" -> "Who.1.How.1"
 * - "Who.1.How.1.What.1.When.1" -> "Who.1.How.1.What.1"
 */
function getParentId(nodeId: string): string | undefined {
  // Root nodes (starting with "Root.") are children of SUCCESS_CRITERIA
  if (nodeId.startsWith('Root.')) {
    return 'SUCCESS_CRITERIA';
  }

  // Split by dots: ["Who", "1", "How", "1"] or ["Who", "1", "How", "1", "What", "1"]
  const parts = nodeId.split('.');

  // Node IDs are in format: Type.Number.Type.Number.Type.Number...
  // To get parent, remove the last Type.Number pair (last 2 elements)
  if (parts.length < 4) {
    // If less than 4 parts, this node ID is malformed or shouldn't have a parent
    return undefined;
  }

  const parentParts = parts.slice(0, -2);

  // If parent has exactly 2 parts (Type.Number), it's a root-level node
  // e.g., ["Who", "1"] should become "Root.Who.1"
  if (parentParts.length === 2) {
    return `Root.${parentParts[0]}.${parentParts[1]}`;
  }

  // For deeper levels, just join the parts
  // e.g., ["Who", "1", "How", "1"] becomes "Who.1.How.1"
  return parentParts.join('.');
}

/**
 * Helper to validate if a string is a valid node ID
 */
export function isValidNodeId(id: string): boolean {
  return /^(Root\.)?[A-Za-z]+(\.\d+)+$/.test(id);
}
